import React, { useEffect, useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useParams, Navigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PageLayout from '../components/PageLayout';
import Section from '../components/PageSection';
import { fetchNewsArticle, CategoryChip, formatNewsDate } from '../news';

const SITE = 'https://qasc.it';

// Article body typography — the JSON html carries only semantic tags
// (p, h2-h4, lists, tables, images, links) produced by the migration.
const Article = styled(Box)(({ theme }) => ({
  maxWidth: '780px',
  margin: '0 auto',
  color: '#33454f',
  fontSize: '16px',
  lineHeight: 1.85,
  '& h2, & h3, & h4': {
    fontFamily: '"Sora", sans-serif',
    color: '#00425c',
    lineHeight: 1.35,
    margin: '1.6em 0 0.6em',
  },
  '& h2': { fontSize: 'clamp(22px, 2.6vw, 27px)' },
  '& h3': { fontSize: 'clamp(19px, 2.2vw, 22px)' },
  '& h4': { fontSize: '18px' },
  '& p': { margin: '0 0 1.1em' },
  '& ul, & ol': { margin: '0 0 1.2em', paddingLeft: '1.4em' },
  '& li': { marginBottom: '0.4em' },
  '& a': {
    color: '#00425c',
    fontWeight: 600,
    textDecorationColor: 'rgba(0,66,92,0.35)',
    '&:hover': { color: '#6d8511' },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '16px',
    margin: '18px 0',
    display: 'block',
  },
  '& blockquote': {
    borderLeft: '3px solid #86a315',
    margin: '1.2em 0',
    padding: '4px 0 4px 18px',
    color: '#48606d',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '1.2em 0',
    fontSize: '14.5px',
  },
  '& td, & th': {
    border: '1px solid rgba(0,66,92,0.15)',
    padding: '8px 12px',
    textAlign: 'left',
  },
}));

function NewsArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setArticle(null);
    setFailed(false);
    fetchNewsArticle(slug)
      .then((a) => alive && setArticle(a))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (failed) return <Navigate to="/news" replace />;

  return (
    <PageLayout
      seo={{
        title: article ? `${article.title} | QAS Consulting` : 'News | QAS Consulting',
        description: article?.excerpt || 'News e pillole di sicurezza di QAS Consulting.',
        canonical: `${SITE}/news/${slug}`,
        image: article?.image ? `${SITE}${article.image}` : undefined,
        type: 'article',
        published: article?.date,
      }}
      hero={{
        kicker: formatNewsDate(article?.date),
        title: article?.title || '…',
      }}
    >
      <Section tone="light">
        <Container maxWidth="lg">
          {!article ? (
            <Typography sx={{ textAlign: 'center', color: '#48606d' }}>
              Caricamento dell’articolo…
            </Typography>
          ) : (
            <Article>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {(article.categories || []).map((c) => (
                  <CategoryChip key={c} label={c} />
                ))}
              </Box>
              {article.image && (
                <Box
                  component="img"
                  src={article.image}
                  alt={article.title}
                  sx={{ width: '100%', borderRadius: '20px', mb: 3, display: 'block' }}
                />
              )}
              {/* Body from the migrated archive JSON (sanitized at build time). */}
              <Box dangerouslySetInnerHTML={{ __html: article.html }} />
              <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid rgba(0,66,92,0.12)' }}>
                <Typography
                  component={RouterLink}
                  to="/news"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    fontFamily: '"Sora", sans-serif',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#00425c',
                    textDecoration: 'none',
                    '&:hover': { color: '#6d8511' },
                  }}
                >
                  <ArrowBackRoundedIcon sx={{ fontSize: '19px' }} />
                  Tutte le news
                </Typography>
              </Box>
            </Article>
          )}
        </Container>
      </Section>
    </PageLayout>
  );
}

export default NewsArticlePage;
